import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Events } from '@pncl/common-models';
import { Observable, Subject, retry, BehaviorSubject, map } from 'rxjs';
import * as moment from 'moment-timezone';
import { Router } from '@angular/router';
import { IFilterAPI } from '../standalones/components/generic-filters-view/filters.interface';
import { Event, EventState, RECURRENCE_ACTION_TYPE, RSVPStatusEnum } from '../models/event';
import { WARNINGS } from '../common/utils/notification-constants';
import { User } from '../models/user';
import {
  ErrorInterceptorSkipHeader,
  httpOptionsWithSkipErrorInterceptor,
} from '../common/functions-and-constant/interceptor';
import { encodeFiltersURI } from './filters.service';
import { URLService } from './dynamic-url.service';
import { NotificationToasterService } from './notification-toaster.service';
import { UserService } from './user.service';
import { TelemetryService } from './telemetry.service';
import { DataSourceFetchParams } from './data-source.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

export enum ScheduleLoadingKey {
  Calendar = 'calendar',
  DeepLink = 'deep-link',
}

@Injectable({
  providedIn: 'root',
})
export class EventService {
  newEventEmitter: EventEmitter<void> = new EventEmitter();
  currentEvent = new Subject<Event | null>();
  user?: User;

  // Events refresh trigger for reactive calendar updates
  private readonly _eventsRefreshTrigger$ = new Subject<void>();
  public readonly eventsRefreshTrigger$: Observable<void> =
    this._eventsRefreshTrigger$.asObservable();

  private readonly _activeLoaders$ = new BehaviorSubject<Set<ScheduleLoadingKey>>(new Set());
  public readonly isScheduleLoading$: Observable<boolean> = this._activeLoaders$.pipe(
    map((loaders) => loaders.size > 0),
  );

  constructor(
    private http: HttpClient,
    private urlService: URLService,
    private notificationToasterService: NotificationToasterService,
    private userService: UserService,
    private router: Router,
    private telemetryService: TelemetryService,
  ) {
    this.user = this.userService.user.value?.user;
  }

  setScheduleLoading(key: ScheduleLoadingKey, isLoading: boolean): void {
    const current = new Set(this._activeLoaders$.value);
    isLoading ? current.add(key) : current.delete(key);
    this._activeLoaders$.next(current);
  }

  triggerEventsRefresh(): void {
    this._eventsRefreshTrigger$.next();
  }

  setCurrentEvent(event: Event): void {
    this.currentEvent.next(event);
    this.router.navigate([], {
      queryParams: { eventId: event._id },
      queryParamsHandling: 'merge',
    });
  }

  clearCurrentEvent(): void {
    this.currentEvent.next(null);
    this.router.navigate([], {
      queryParams: { eventId: null },
      queryParamsHandling: 'merge',
    });
  }

  openCreateNewEvent() {
    this.notificationToasterService.dismissNotificationsByCode([WARNINGS.EVENTS_FETCH_LIMITED]);
    this.newEventEmitter.emit();
  }

  createEvent(event: Event): Observable<any> {
    const createEventUrl = `${this.urlService.getDynamicUrl()}/events/`;
    return this.http
      .post(createEventUrl, JSON.stringify(event), {
        ...httpOptions,
        observe: 'response',
      })
      .pipe(retry(3));
  }

  getEventsUsingFilters(
    start: string,
    end: string,
    filters?: IFilterAPI,
    pageNumber = 1,
  ): Observable<any> {
    return this.getAllEvents(start, end, filters, pageNumber);
  }

  getAllEvents(start: string, end: string, filters?: IFilterAPI, pageNumber = 1): Observable<any> {
    let params = new HttpParams().set('start', start).set('end', end);
    if (filters) {
      const encodedFilters = btoa(encodeURIComponent(JSON.stringify(filters)));
      params = params.set('filters', encodedFilters).set('pageNumber', pageNumber);
    }

    return this.http.get(`${this.urlService.getDynamicUrl()}/events/all`, { params });
  }

  getEventState(id: string): Observable<EventState> {
    const getEventStateUrl = `${this.urlService.getDynamicUrl()}/events/${id}/state`;
    return this.http.get<EventState>(getEventStateUrl);
  }

  getNextSpaceEvents(windowMinutes?: number): Observable<Events.NextSpace.NextSpaceEvent[]> {
    const getActiveEventUrl = `${this.urlService.getDynamicUrl()}/events/event-next-space`;
    const params: Record<string, any> = {};

    if (windowMinutes) {
      params['windowMinutes'] = windowMinutes;
    }

    return this.http.get<Events.NextSpace.NextSpaceEvent[]>(getActiveEventUrl, {
      headers: { [ErrorInterceptorSkipHeader]: '' },
      params,
    });
  }

  getEvent(id: string): Observable<Event> {
    return this.http.get<Event>(`${this.urlService.getDynamicUrl()}/events/${id}`, {
      ...httpOptionsWithSkipErrorInterceptor,
    });
  }

  deleteEvent(id: string, deleteType = RECURRENCE_ACTION_TYPE.CURRENT): Observable<any> {
    const deleteEventUrl = `${this.urlService.getDynamicUrl()}/events/${id}`;
    return this.http.delete(deleteEventUrl, {
      body: { deleteType },
    });
  }

  editEvent(event: Event, editType = RECURRENCE_ACTION_TYPE.CURRENT): Observable<any> {
    const editEventUrl = `${this.urlService.getDynamicUrl()}/events/${event._id}`;
    return this.http.patch(editEventUrl, {
      event,
      editType,
    });
  }

  endEvent(eventId: string): Observable<any> {
    const endEventUrl = `${this.urlService.getDynamicUrl()}/events/${eventId}/end-event`;
    return this.http.post(endEventUrl, {});
  }

  extendEvent(eventId: string, extensionSeconds: number): Observable<any> {
    return this.http.post<any>(
      `${this.urlService.getDynamicUrl()}/events/${eventId}/extend-event`,
      {
        extensionSeconds,
      },
    );
  }

  setMomentDefaultTimezone(timezoneID: string) {
    moment.tz.setDefault(timezoneID);
  }

  setMomentDefaultToUserTimezone(): void {
    if (!this.user) {
      return;
    }

    moment.tz.setDefault(this.user.timezone.value);
  }

  cancelEvent(eventId: string, note: string): Observable<any> {
    const cancelEventUrl = `${this.urlService.getDynamicUrl()}/events/${eventId}/cancel`;
    return this.http.post(cancelEventUrl, { note });
  }

  changeRsvpStatus(
    eventId: string,
    userId: string,
    status: RSVPStatusEnum,
    note?: string,
  ): Observable<any> {
    const changeRsvpStatusUrl = `${this.urlService.getDynamicUrl()}/events/${eventId}/rsvp`;
    return this.http.post(changeRsvpStatusUrl, { user_id: userId, note, status });
  }

  getReassignableEvents(eventId: string, params: DataSourceFetchParams): Observable<any> {
    return this.http.get(`${this.urlService.getDynamicUrl()}/events/${eventId}/reassign-events`, {
      params: {
        filters: encodeFiltersURI(params.filter),
        pageNumber: params.pageIndex + 1,
        pageSize: params.pageSize,
      },
    });
  }

  reassignUser(eventId: string, targetEventId: string, userId: string): Observable<any> {
    const reassignUserUrl = `${this.urlService.getDynamicUrl()}/events/${eventId}/reassign-user`;
    return this.http.post(
      reassignUserUrl,
      { targetEventId, userId },
      httpOptionsWithSkipErrorInterceptor,
    );
  }

  /**
   * Logs telemetry data for NextSpace event operations.
   *
   * @param logObject - Object containing key-value pairs to log
   */
  public nextSpaceLog(logObject: { [key: string]: any }) {
    this.telemetryService.logBasicEvent('[next-space-redirect-monitoring]', logObject);
  }
}
